import React from 'react';
import { useSelector } from 'react-redux';
import { GetServerSideProps } from 'next';
import { useDecision } from '@optimizely/react-sdk';

import Page from '@noqo/clients/web/Page';

import initializePage from '@/common/pageSetup';
import CenteredContainer from '@/common/CenteredContainer';
import logger from '@/common/logger';

import Menu from '@/menu/Menu';
import LocalizedMenu from '@/menu/deals/LocalizedMenu';
import DealBuilder from '@/builders/deals/DealBuilder';

import dealService from '@/services/dealService';
import { ErrorPage } from '@/ErrorPage';

import { WHITELISTED_DEALS } from '@/deals/constants';
import { useDealsPage } from '@/deals/hooks/useDealsPage';
import { localizationSelectors } from '@/localization/localizationSelectors';

export interface DealsProps {
  dealGlobalId?: string;
  ssrPrivateCode?: string | null;
  ssrError?: boolean;
  hasValidPrivateCode?: boolean;
  id: string | null;
}

const DealsPage = ({
  dealGlobalId,
  ssrPrivateCode,
  ssrError,
  hasValidPrivateCode,
  id
}: DealsProps): JSX.Element => {
  const {
    isError,
    isDealBuilderPage,
    dealId,
    userIsLocalized,
    updatedNationalDeals
  } = useDealsPage({
    dealGlobalId, ssrPrivateCode, ssrError, hasValidPrivateCode, id
  });

  if (isError) {
    return <ErrorPage />;
  }

  // deal builder flow
  if (isDealBuilderPage) {
    return (
      <>
        <DealBuilder privateCode={dealId || ''} publicCode={dealGlobalId || ''} />
      </>
    );
  }

  // list of localized or national deals flow
  return (
    <>
      <CenteredContainer>
        {userIsLocalized
          ? <LocalizedMenu />
          : (
            <Menu
              itemsTestId="national"
              title="Deals"
              items={updatedNationalDeals}
              caloriesDisclaimer
              isNational
              isDeals
            />
          )}
      </CenteredContainer>
    </>
  );
};

const Deals = (props: DealsProps): JSX.Element => {
  const [{ enabled: isNoqoDealsEnabled }] = useDecision('fr-noqo-86-deals-enabled-2');
  const userIsLocalized = useSelector(localizationSelectors.isLocalized);
  

  if (
    userIsLocalized &&
    isNoqoDealsEnabled
  ) {
    return <Page slug="deals" />; // TODO: NOQO - Replace with actual page slug
  }

  return <DealsPage {...props} />
}

export default Deals;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const { props } = await (initializePage(ctx, 'deals') as any);
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const discountCode = (ctx as any)?.query?.dealId || '';

  const dealGlobalId = (ctx as any)?.query?.id || (ctx as any)?.query?.dealId || '';
  const storeId = props.storeNumber;
  const { 'localization-token': localizationToken } = ctx.req.cookies;

  if (discountCode) {
    return {
      props: {
        ...props,
        dealGlobalId,
        ssrPrivateCode: discountCode,
        hasValidPrivateCode: true
      }
    };
  }

  if (dealGlobalId && storeId && localizationToken) {
    try {
      const { privateCode } = await dealService.getPrivateCode(
        storeId,
        dealGlobalId,
        localizationToken
      );

      const isWhitelisted = !!privateCode && WHITELISTED_DEALS.includes(privateCode);
      const isValidPrivateCode = !!privateCode && (privateCode !== dealGlobalId || isWhitelisted);

      if (privateCode === dealGlobalId && !isWhitelisted) {
        logger.error(
          new Error('User prohibited from visiting deal because public code === private code'),
          { 'public code': dealGlobalId, 'private code': privateCode }
        );
      }

      return {
        props: {
          ...props,
          dealGlobalId,
          ssrPrivateCode: privateCode ?? null,
          hasValidPrivateCode: isValidPrivateCode ?? null
        }
      };
    } catch (error) {
      logger.withoutTelemetry.error('Failed to get deal ID: ', error);
      return {
        props: {
          ...props,
          dealGlobalId,
          ssrError: true
        }
      };
    }
  }
  return {
    props: {
      ...props,
      dealGlobalId
    }
  };
};
